import { ComponentType, useLayoutEffect, useMemo } from 'react'
import useLatest from 'react-use/lib/useLatest'
import { v4 } from 'uuid'
import { SceneElement, useControlStore } from '@editorStores'

/**
 * Registers `component` with `properties` as a scene element while enabled. Always memo the
 * properties that go into this hook.
 *
 * The element keeps its id while mounted and new properties only replace its properties, so the
 * component re-renders instead of remounting (no rebuilt meshes while e.g. a form is edited).
 * `updateInPlace: false` re-registers it under a new id instead, remounting the component: for
 * components that rely on the remount to reset local state (SlabDrawing, RoofDrawing).
 */
const useSceneElement = <Props extends object>({
  enabled,
  properties,
  component,
  updateInPlace = true,
}: {
  enabled: boolean
  properties: Props
  component: ComponentType<Props>
  updateInPlace?: boolean
}) => {
  const addSceneElement = useControlStore(state => state.addSceneElement)
  const removeSceneElement = useControlStore(state => state.removeSceneElement)
  const updateSceneElement = useControlStore(state => state.updateSceneElement)

  const stableId = useMemo(() => v4(), [])
  const latestProperties = useLatest(properties)
  // Without `updateInPlace`, new properties re-run the registration below (new id, remount)
  const registrationProperties = updateInPlace ? null : properties

  useLayoutEffect(() => {
    if (!enabled) return

    const id = updateInPlace ? stableId : v4()

    const sceneElement: SceneElement<Props> = {
      id,
      properties: latestProperties.current,
      component,
    }

    addSceneElement(sceneElement)

    return () => removeSceneElement(id)
  }, [
    addSceneElement,
    component,
    enabled,
    latestProperties,
    registrationProperties,
    removeSceneElement,
    stableId,
    updateInPlace,
  ])

  useLayoutEffect(() => {
    if (enabled && updateInPlace) updateSceneElement(stableId, properties)
  }, [enabled, properties, stableId, updateInPlace, updateSceneElement])
}

export default useSceneElement
